import {
  loadPdfDocument,
  pickSafeRenderScale,
  type PdfDocumentProxy,
} from '@ironview/interactive-viewer';

export interface CropRegion {
  /** x offset in PDF points from the page's top-left */
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Renders a rectangular region of a PDF page to a PNG Blob.
 *
 * @param fileUrl    Signed URL of the PDF file
 * @param pageNumber 1-indexed page number
 * @param region     Rectangle in PDF-point coordinates (from the page's top-left)
 * @param outputDpi  Render resolution. When omitted, a device-aware scale is
 *                   chosen that stays under iOS Safari's canvas limits.
 * @param sharedDoc  Optional pre-loaded PDF document. When provided the PDF
 *                   is NOT re-parsed (which would otherwise double the memory
 *                   footprint and is one of the main causes of the iOS Safari
 *                   OOM crash on PDF layouts).
 */
export async function cropPdfRegionToBlob(
  fileUrl: string,
  pageNumber: number,
  region: CropRegion,
  outputDpi?: number,
  sharedDoc?: PdfDocumentProxy | null,
  rotation = 0,
): Promise<Blob> {
  const doc = sharedDoc ?? (await loadPdfDocument(fileUrl));
  const page = await doc.getPage(pageNumber);

  const desiredScale = outputDpi ? outputDpi / 72 : 1; // 72dpi == 1× == screen-equivalent
  const scale = pickSafeRenderScale(region.width, region.height, desiredScale);
  const fullViewport = page.getViewport({ scale, rotation });

  const canvasW = Math.round(region.width * scale);
  const canvasH = Math.round(region.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = canvasW;
  canvas.height = canvasH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get 2D canvas context');

  ctx.translate(-region.x * scale, -region.y * scale);

  await page.render({ canvasContext: ctx, viewport: fullViewport }).promise;

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      // Release the backing pixel buffer before the PNG is decoded by an
      // <img>. WebKit otherwise tends to retain both allocations until GC.
      canvas.width = 0;
      canvas.height = 0;
      if (blob) resolve(blob);
      else reject(new Error('canvas.toBlob returned null'));
    }, 'image/png');
  });
}

/**
 * Renders a full PDF page to PNG Blob.
 *
 * @param fileUrl    Signed URL of the PDF file
 * @param pageNumber 1-indexed page number
 * @param outputDpi  Render resolution. When omitted, a device-aware scale is
 *                   chosen that stays under iOS Safari's canvas limits.
 * @param sharedDoc  Optional pre-loaded PDF document. When provided the PDF
 *                   is NOT re-parsed.
 */
export async function renderPdfPageToBlob(
  fileUrl: string,
  pageNumber: number,
  outputDpi?: number,
  sharedDoc?: PdfDocumentProxy | null,
  rotation = 0,
): Promise<Blob> {
  const doc = sharedDoc ?? (await loadPdfDocument(fileUrl));
  const page = await doc.getPage(pageNumber);

  const naturalViewport = page.getViewport({ scale: 1, rotation });
  const desiredScale = outputDpi ? outputDpi / 72 : 1;
  const scale = pickSafeRenderScale(
    naturalViewport.width,
    naturalViewport.height,
    desiredScale,
  );
  const viewport = page.getViewport({ scale, rotation });

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get 2D canvas context');

  await page.render({ canvasContext: ctx, viewport }).promise;

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      canvas.width = 0;
      canvas.height = 0;
      if (blob) resolve(blob);
      else reject(new Error('canvas.toBlob returned null'));
    }, 'image/png');
  });
}
